import { useQuery } from '@tanstack/react-query';
import { Bot, ListChecks, CheckCircle2, ClipboardCheck, AlertTriangle } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';

// Mirrors the GET /dashboard handler in functions/src/routes/admin/agent.ts.
interface AgentStats {
  active_tasks: number;
  orders_processing: number;
  // numeric(5,1) — may serialize as a string from postgres.js
  success_rate_24h: number | string;
  avg_fulfillment_hours: number | string;
  pipeline_orders_24h: number;
  pipeline_success_rate: number | string;
  unread_notifications: number;
  prospects_total: number;
  pending_approvals: number;
}

interface EscalatedTask {
  id: string;
}

interface FailedOrder {
  id: string;
}

interface AgentDashboardResponse {
  stats: AgentStats;
  recent_activity: unknown[];
  needs_attention: {
    escalated_tasks: EscalatedTask[];
    failed_orders: FailedOrder[];
  };
}

export default function AgentActivityWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'agent', 'dippy'],
    queryFn: () => api.get<AgentDashboardResponse>('/admin/agent/dashboard?agent_id=dippy'),
    staleTime: 60000,
  });

  const needsAttention = data
    ? data.needs_attention.escalated_tasks.length + data.needs_attention.failed_orders.length
    : 0;

  const successRate = data ? Math.round(Number(data.stats.success_rate_24h ?? 0)) : 0;

  const isEmpty =
    !!data &&
    data.stats.active_tasks === 0 &&
    data.stats.pending_approvals === 0 &&
    needsAttention === 0 &&
    data.recent_activity.length === 0;

  return (
    <WidgetShell
      title="Agent activity"
      icon={<Bot />}
      to="/admin/agent"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="No agent activity yet."
      emptyIcon={<Bot />}
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="Active tasks"
            value={data.stats.active_tasks}
            icon={<ListChecks />}
            tone="blue"
          />
          <StatCard
            title="Success rate 24h"
            value={`${successRate}%`}
            icon={<CheckCircle2 />}
            tone="green"
          />
          <StatCard
            title="Pending approvals"
            value={data.stats.pending_approvals}
            icon={<ClipboardCheck />}
            tone="amber"
          />
          <StatCard
            title="Needs attention"
            value={needsAttention}
            icon={<AlertTriangle />}
            tone={needsAttention > 0 ? 'red' : 'stone'}
          />
        </div>
      )}
    </WidgetShell>
  );
}
